/**
 * StagingOnly - HOC component that guards staging-only features
 *
 * This component ensures that certain features are only accessible
 * when the application is running in staging mode (previewDrafts perspective).
 *
 * On production (published perspective), users are redirected to the home page.
 *
 * Requirement: ES-REQ-076, ES-REQ-079
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface StagingOnlyProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export const StagingOnly: React.FC<StagingOnlyProps> = ({
  children,
  redirectTo = '/'
}) => {
  const navigate = useNavigate();
  const perspective = import.meta.env.VITE_SANITY_PERSPECTIVE;

  // Check if we're in staging mode (previewDrafts perspective)
  const isStaging = perspective === 'previewDrafts';

  useEffect(() => {
    if (!isStaging) {
      // Not in staging mode - redirect to home
      console.log('Labeling feature is only available in staging mode');
      navigate(redirectTo, { replace: true });
    }
  }, [isStaging, navigate, redirectTo]);

  // Only render children if in staging mode
  if (!isStaging) {
    return null;
  }

  return <>{children}</>;
};

export default StagingOnly;
